import { useState, useEffect } from 'react';
import { useIsMobile } from '../hooks/useIsMobile';
import toast from 'react-hot-toast';
import { api } from '../lib/api';
import { format } from 'date-fns';

const today = () => new Date().toISOString().split('T')[0];

const emptyRow = () => ({ date: today(), description: '', amount: '', category_id: '', notes: '' });

const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Math.abs(n));

export default function CashTransactionsPage() {
  const [tab, setTab] = useState('single');
  const isMobile = useIsMobile();
  const [categories, setCategories] = useState([]);
  const [saving, setSaving] = useState(false);
  const [recentManual, setRecentManual] = useState([]);

  // Single form
  const [form, setForm] = useState({
    date: today(), description: '', amount: '', category_id: '',
    notes: '', is_recurring: false, type: 'expense',
  });

  // Bulk rows
  const [rows, setRows] = useState([emptyRow(), emptyRow(), emptyRow()]);

  useEffect(() => {
    api.getCategories().then(setCategories);
    loadRecent();
  }, []);

  const loadRecent = async () => {
    try {
      const txs = await api.getTransactions({ source: 'manual' });
      setRecentManual(txs.slice(0, 10));
    } catch {}
    try {
      const bulk = await api.getTransactions({ source: 'bulk' });
      setRecentManual(prev => [...prev, ...bulk.slice(0, 10)]
        .sort((a, b) => new Date(b.date) - new Date(a.date))
        .slice(0, 15));
    } catch {}
  };

  const setF = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const saveSingle = async () => {
    if (!form.description || !form.amount) return toast.error('Description and amount required');
    setSaving(true);
    try {
      const amount = form.type === 'expense'
        ? -Math.abs(parseFloat(form.amount))
        : Math.abs(parseFloat(form.amount));

      await api.addManualTransaction({
        date: form.date,
        description: form.description,
        amount,
        category_id: form.category_id ? parseInt(form.category_id) : null,
        notes: form.notes || null,
        is_recurring: form.is_recurring,
      });

      toast.success('Transaction added!');
      setForm({ date: today(), description: '', amount: '', category_id: '', notes: '', is_recurring: false, type: 'expense' });
      loadRecent();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const saveBulk = async () => {
    const valid = rows.filter(r => r.description && r.amount);
    if (valid.length === 0) return toast.error('Add at least one row');
    setSaving(true);
    try {
      const payload = valid.map(r => ({
        date: r.date,
        description: r.description,
        amount: parseFloat(r.amount),   // let user enter +/- directly in bulk
        category_id: r.category_id ? parseInt(r.category_id) : null,
        notes: r.notes || null,
      }));
      const res = await api.addBulkTransactions(payload);
      toast.success(`${res.created} transactions added!`);
      setRows([emptyRow(), emptyRow(), emptyRow()]);
      loadRecent();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const updateRow = (i, k, v) => setRows(rs => rs.map((r, j) => j === i ? { ...r, [k]: v } : r));
  const addRow = () => setRows(rs => [...rs, emptyRow()]);
  const removeRow = (i) => setRows(rs => rs.filter((_, j) => j !== i));

  const deleteRecent = async (id) => {
    await api.deleteTransaction(id);
    setRecentManual(r => r.filter(t => t.id !== id));
    toast.success('Deleted');
  };

  return (
    <div className="animate-in">
      <div className="page-title">Cash Transactions</div>
      <div className="page-subtitle">Log transactions that don't appear on your credit card or bank statement</div>

      <div className="tabs">
        <button className={`tab ${tab === 'single' ? 'active' : ''}`} onClick={() => setTab('single')}>Single Entry</button>
        <button className={`tab ${tab === 'bulk' ? 'active' : ''}`} onClick={() => setTab('bulk')}>Bulk Entry</button>
      </div>

      {tab === 'single' && (
        <div className="card mb-6 animate-in" style={{ maxWidth: 560 }}>
          <div className="section-title mb-4">Add Cash Transaction</div>

          {/* Expense / Income toggle */}
          <div className="form-group">
            <label className="form-label">Type</label>
            <div style={{ display: 'flex', gap: 8 }}>
              {['expense', 'income'].map(t => (
                <button
                  key={t}
                  onClick={() => setForm(f => ({ ...f, type: t }))}
                  style={{
                    padding: '7px 20px', borderRadius: 6, border: '1px solid',
                    borderColor: form.type === t ? (t === 'expense' ? 'var(--red)' : 'var(--green)') : 'var(--border-bright)',
                    background: form.type === t ? (t === 'expense' ? 'rgba(245,101,101,0.1)' : 'rgba(62,207,142,0.1)') : 'transparent',
                    color: form.type === t ? (t === 'expense' ? 'var(--red)' : 'var(--green)') : 'var(--text-secondary)',
                    cursor: 'pointer', fontSize: 13, fontWeight: 500, textTransform: 'capitalize',
                    fontFamily: 'var(--font-body)',
                  }}
                >
                  {t === 'expense' ? '↓ Expense' : '↑ Income'}
                </button>
              ))}
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 16 }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">Date</label>
              <input className="input" type="date" value={form.date} onChange={setF('date')} />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">Amount ($)</label>
              <input className="input" type="number" step="0.01" min="0" placeholder="0.00" value={form.amount} onChange={setF('amount')} />
            </div>
          </div>

          <div className="form-group mt-4">
            <label className="form-label">Description</label>
            <input className="input" placeholder='e.g. Farmers market, Babysitter, ATM withdrawal' value={form.description} onChange={setF('description')} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 16 }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">Category</label>
              <select className="input" value={form.category_id} onChange={setF('category_id')}>
                <option value="">Uncategorized</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.icon} {c.name}</option>)}
              </select>
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">Notes (optional)</label>
              <input className="input" placeholder="Any extra context" value={form.notes} onChange={setF('notes')} />
            </div>
          </div>

          <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 16, cursor: 'pointer', fontSize: 13, color: 'var(--text-secondary)' }}>
            <input type="checkbox" checked={form.is_recurring} onChange={e => setForm(f => ({ ...f, is_recurring: e.target.checked }))} />
            Mark as recurring
          </label>

          <button className="btn btn-primary mt-4" onClick={saveSingle} disabled={saving || !form.description || !form.amount}>
            {saving ? 'Saving…' : '+ Add Transaction'}
          </button>
        </div>
      )}

      {tab === 'bulk' && (
        <div className="card mb-6 animate-in">
          <div className="flex justify-between items-center mb-4">
            <div className="section-title" style={{ marginBottom: 0 }}>Bulk Entry — use negative amounts for expenses, positive for income</div>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  {['Date', 'Description', 'Amount ($)', 'Category', 'Notes', ''].map(h => (
                    <th key={h} style={{ textAlign: 'left', padding: '6px 8px', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => (
                  <tr key={i}>
                    <td style={{ padding: '5px 6px' }}>
                      <input className="input" type="date" value={row.date} onChange={e => updateRow(i, 'date', e.target.value)} style={{ width: 135 }} />
                    </td>
                    <td style={{ padding: '5px 6px' }}>
                      <input className="input" placeholder="Description" value={row.description} onChange={e => updateRow(i, 'description', e.target.value)} style={{ width: 200 }} />
                    </td>
                    <td style={{ padding: '5px 6px' }}>
                      <input className="input" type="number" step="0.01" placeholder="-50.00" value={row.amount} onChange={e => updateRow(i, 'amount', e.target.value)} style={{ width: 110 }} />
                    </td>
                    <td style={{ padding: '5px 6px' }}>
                      <select className="input" value={row.category_id} onChange={e => updateRow(i, 'category_id', e.target.value)} style={{ width: 160 }}>
                        <option value="">—</option>
                        {categories.map(c => <option key={c.id} value={c.id}>{c.icon} {c.name}</option>)}
                      </select>
                    </td>
                    <td style={{ padding: '5px 6px' }}>
                      <input className="input" placeholder="Optional" value={row.notes} onChange={e => updateRow(i, 'notes', e.target.value)} style={{ width: 140 }} />
                    </td>
                    <td style={{ padding: '5px 6px' }}>
                      <button className="btn btn-sm btn-danger" onClick={() => removeRow(i)}>×</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex gap-3 mt-4">
            <button className="btn btn-ghost btn-sm" onClick={addRow}>+ Add Row</button>
            <button className="btn btn-primary" onClick={saveBulk} disabled={saving}>
              {saving ? 'Saving…' : `Save ${rows.filter(r => r.description && r.amount).length} Transactions`}
            </button>
          </div>
        </div>
      )}

      {/* Recent manual entries */}
      {recentManual.length > 0 && (
        <div className="card" style={{ padding: 0 }}>
          <div style={{ padding: '16px 20px 12px', borderBottom: '1px solid var(--border)' }}>
            <div className="section-title" style={{ marginBottom: 0 }}>Recent Manual Entries</div>
          </div>
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Description</th>
                <th>Category</th>
                <th>Source</th>
                <th style={{ textAlign: 'right' }}>Amount</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {recentManual.map(tx => (
                <tr key={tx.id}>
                  <td style={{ color: 'var(--text-muted)', fontSize: 12 }}>{format(new Date(tx.date), 'MMM d')}</td>
                  <td style={{ fontSize: 13.5 }}>{tx.description}</td>
                  <td>
                    {tx.category_name
                      ? <span className="cat-pill" style={{ borderLeft: `3px solid ${tx.category_color}` }}>{tx.category_icon} {tx.category_name}</span>
                      : <span className="badge badge-yellow">Uncategorized</span>}
                  </td>
                  <td>
                    <span style={{ fontSize: 11, padding: '2px 7px', borderRadius: 4, background: 'var(--bg-elevated)', color: 'var(--text-muted)' }}>
                      {tx.source === 'bulk' ? 'bulk' : 'manual'}
                    </span>
                  </td>
                  <td className="text-right">
                    <span className={tx.amount >= 0 ? 'amount-pos' : 'amount-neg'}>
                      {tx.amount >= 0 ? '+' : '-'}{fmt(tx.amount)}
                    </span>
                  </td>
                  <td>
                    <button className="btn btn-sm btn-danger" onClick={() => deleteRecent(tx.id)}>×</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
